import { currentSessionSignal, isSessionEndedError, sessionEndedSince, SessionEndedError, sessionFetch } from '@papan/api-client'

/**
 * The share-view chrome's in-place open file (`?file=` + `openFile` state) — the two
 * transitions that must keep the header, the explorer highlight and Save/Discard on the
 * SAME file the body is showing. Pure / injectable so they are unit-tested without
 * mounting the (heavy) chrome.
 */

/**
 * What `openFile` becomes when opening `requestedId` FAILED.
 *
 * The chrome keeps the previous file on screen while the next one loads ("swap
 * atomically"), so a re-mint of the SAME file (identity re-bind) never blanks the
 * overlay — that file is still the right one, keep it. But when the request was for a
 * DIFFERENT file, keeping the old one leaves the header, the tree highlight and
 * Save/Discard on file A while the body shows file B's error: clear it, so everything
 * follows `?file=`.
 */
export function openFileAfterFailure<T extends { fileId: string }>(
  prev: T | null,
  requestedId: string,
): T | null {
  if (!prev) return null
  return prev.fileId === requestedId ? prev : null
}

interface InfoWithFile { file: { id: string; name: string } }

/**
 * The session info for `fileId` after it was renamed to `name` through the explorer.
 *
 * Refetches `/info` for the file's session (`sid`) so everything derived from the info
 * — the header, the serve / download / probe URLs, the editor chosen by extension —
 * follows the new name. A failed or mismatched refetch falls back to patching the name
 * into the info the chrome already holds (never leaves the old name on screen) — except when it failed because the
 * session it was asked in ended (#1316): that rejects with the `SessionEndedError`, and the caller writes nothing.
 */
export async function refreshRenamedSessionInfo<I extends InfoWithFile>(
  sid: string,
  current: I,
  fileId: string,
  name: string,
  infoUrl: (sid: string) => string,
  fetchImpl: typeof fetch = sessionFetch,   // #1316: bound to the session it starts in
): Promise<I> {
  const patched = { ...current, file: { ...current.file, name } } as I
  const start = currentSessionSignal()
  try {
    const res = await fetchImpl(infoUrl(sid), { credentials: 'include' })
    if (!res.ok) return patched
    const body = (await res.json()) as I
    if (body?.file?.id !== fileId) return patched
    return body
  } catch (err) {
    if (sessionEndedSince(start, err)) throw isSessionEndedError(err) ? err : new SessionEndedError()
    return patched
  }
}
